import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./ChatHistoryView.css"; // CSS siehe unten
import {
    ApproveToolCallRequestModel,
    ChatMessageModel,
    IApproveToolCallRequestModel,
    PdfRenderRequest,
    PdfShareRequest,
    ToolCallViewModel
} from "../../client/SmartAssistantBackendClient";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { ClientHelper } from "../../client/CreateNswagClient";
import { useLoginContext } from "../login/LoginHandler";
import { Components } from "react-markdown";
import { PagingControls } from "../paging/PagingControls";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import { ChatHistoryMessage } from "./ChatHistoryMessage";
import { useCurrentThreadContext } from "../../contexts/CurrentThreadContext";
import { PeoplePicker } from "../app/PeoplePicker";


const ChatHistoryViewImpl: React.FC = () => {
    const { history: messages, threadId: historyKey, approveToolCall } = useCurrentThreadContext();
    const tokenContext = useLoginContext();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const autoScrollRef = useRef(true);
    const [showToolCalls, setShowToolCalls] = useState(false);
    const [pendingActions, setPendingActions] = useState<Record<string, "approving" | "rejecting">>({});
    const hasRenderableContent = (value?: string | null) =>
        typeof value === "string" && value.trim() !== "";

    const filteredMessages = useMemo(() => (
        messages.filter(m => {
            const hasContent = hasRenderableContent(m.content);
            const hasDownload = Boolean(m.downloadRequest?.markdownContent);
            const hasApprovalToolCall = m.toolCalls?.some(tc => tc?.callNeedsToBeValidated) ?? false;
            const hasApprovalStatus = m.toolCalls?.some(tc => tc?.approvalStatus === "approved" || tc?.approvalStatus === "rejected") ?? false;
            const shouldShowToolCalls = showToolCalls && (m.toolCalls !== undefined && m.toolCalls.length > 0);
            return hasContent
                || hasDownload
                || shouldShowToolCalls
                || hasApprovalToolCall
                || hasApprovalStatus
                || (m?.clarification?.questions?.length);
        })
    ), [messages, showToolCalls]);

    const WINDOW_SIZE = 6;
    const WINDOW_THRESHOLD = 10;
    const totalMessages = filteredMessages.length;
    const useWindowing = totalMessages > WINDOW_THRESHOLD;
    const maxWindowStart = Math.max(0, totalMessages - WINDOW_SIZE);
    const [windowStart, setWindowStart] = useState(0);
    const [pinnedToLatest, setPinnedToLatest] = useState(true);

    useEffect(() => {
        setPinnedToLatest(true);
    }, [historyKey]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const host = window as any;
        if (!host.smartAssistant) {
            host.smartAssistant = {};
        }
        host.smartAssistant.showToolCalls = showToolCalls;
        host.smartAssistant.toggleToolCalls = () => {
            setShowToolCalls((prev) => {
                const next = !prev;
                host.smartAssistant.showToolCalls = next;
                return next;
            });
        };
        host.smartAssistant.setToolCallsVisible = (next: boolean) => {
            const value = Boolean(next);
            host.smartAssistant.showToolCalls = value;
            setShowToolCalls(value);
        };
        return () => {
            if (host.smartAssistant?.toggleToolCalls) {
                delete host.smartAssistant.toggleToolCalls;
            }
            if (host.smartAssistant?.setToolCallsVisible) {
                delete host.smartAssistant.setToolCallsVisible;
            }
        };
    }, [showToolCalls]);

    useEffect(() => {
        if (!useWindowing) {
            setWindowStart(0);
            setPinnedToLatest(true);
            return;
        }
        if (pinnedToLatest) {
            setWindowStart(maxWindowStart);
            return;
        }
        setWindowStart((prev) => Math.min(prev, maxWindowStart));
    }, [maxWindowStart, pinnedToLatest, useWindowing]);

    const handlePrevWindow = useCallback(() => {
        if (!useWindowing) return;
        setPinnedToLatest(false);
        setWindowStart((prev) => Math.max(0, prev - WINDOW_SIZE));
    }, [useWindowing]);

    const handleNextWindow = useCallback(() => {
        if (!useWindowing) return;
        setWindowStart((prev) => {
            const next = Math.min(maxWindowStart, prev + WINDOW_SIZE);
            if (next >= maxWindowStart) {
                setPinnedToLatest(true);
            }
            return next;
        });
    }, [maxWindowStart, useWindowing]);

    const windowedMessages = useMemo(() => {
        if (!useWindowing) return filteredMessages;
        return filteredMessages.slice(windowStart, windowStart + WINDOW_SIZE);
    }, [filteredMessages, useWindowing, windowStart]);
    const normalizeContent = useCallback((value?: string | null) => {
        if (!value) return "";
        return value.replace(/\u00ad/g, "");
    }, []);
    const extractText = useCallback((node: React.ReactNode): string => {
        if (node === null || node === undefined) return "";
        if (typeof node === "string" || typeof node === "number") return String(node);
        if (Array.isArray(node)) return node.map(extractText).join("");
        if (React.isValidElement(node)) {
            const element = node as React.ReactElement<{ children?: React.ReactNode }>;
            return extractText(element.props.children);
        }
        return "";
    }, []);
    const detectCalloutType = useCallback((text: string): null | {
        type: string;
        title: string;
        icon: React.ReactNode;
    } => {
        const normalized = text.trim().toLowerCase();
        const start = normalized.replace(/^[\s>]+/, "");
        if (start.startsWith("hinweis") || start.startsWith("note") || start.startsWith("info")) {
            return { type: "info", title: "Hinweis", icon: <InfoOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("tipp") || start.startsWith("tip")) {
            return { type: "tip", title: "Tipp", icon: <TipsAndUpdatesOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("warnung") || start.startsWith("achtung") || start.startsWith("warning") || start.startsWith("caution")) {
            return { type: "warning", title: "Warnung", icon: <WarningAmberOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("idee") || start.startsWith("insight") || start.startsWith("idea")) {
            return { type: "idea", title: "Idee", icon: <LightbulbOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("empfehlung") || start.startsWith("pro") || start.startsWith("good")) {
            return { type: "success", title: "Empfehlung", icon: <ThumbUpOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("zusammenfassung") || start.startsWith("summary")) {
            return { type: "summary", title: "Zusammenfassung", icon: <SummarizeOutlinedIcon fontSize="small" /> };
        }
        if (start.startsWith("ergebnis") || start.startsWith("fazit") || start.startsWith("result")) {
            return { type: "result", title: "Ergebnis", icon: <CheckCircleOutlineOutlinedIcon fontSize="small" /> };
        }
        return null;
    }, []);
    const markdownComponents: Components = useMemo(() => ({
        a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<"a">) => (
            <a href={href} target="_blank" rel="noreferrer" {...props}>
                {children}
            </a>
        ),
        blockquote: ({ children }: { children?: React.ReactNode }) => {
            const text = extractText(children);
            const callout = detectCalloutType(text);
            if (!callout) return <blockquote>{children}</blockquote>;
            return (
                <div className={`callout callout-${callout.type}`}>
                    <div className="callout-header">
                        <span className="callout-icon">{callout.icon}</span>
                        <span className="callout-title">{callout.title}</span>
                    </div>
                    <div className="callout-body">{children}</div>
                </div>
            );
        },
        code: ({ children, ...props }: React.ComponentPropsWithoutRef<"code">) => (
            <code {...props}>{children}</code>
        ),
        pre: () => null,
    }), [detectCalloutType, extractText]);
    const handlePdfDownload = useCallback(async (payload: { fileName?: string; markdownContent?: string }) => {
        try {
            const token = tokenContext.getUserToken();
            const pdfClient = ClientHelper.createPdfClient(token);
            const response = await pdfClient.render(new PdfRenderRequest({
                fileName: payload.fileName,
                markdownContent: payload.markdownContent,
            }));
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = response.fileName ?? payload.fileName ?? "export.pdf";
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                URL.revokeObjectURL(url);
                link.remove();
            }, 0);
        } catch (err) {
            console.error("PDF download failed", err);
        }
    }, [tokenContext]);

    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareRecipients, setShareRecipients] = useState<string[]>([]);
    const [shareSubject, setShareSubject] = useState("PDF Export");
    const [shareTarget, setShareTarget] = useState<{ fileName?: string; markdownContent?: string } | null>(null);
    const [shareSending, setShareSending] = useState(false);
    const [pdfMenuAnchor, setPdfMenuAnchor] = useState<HTMLElement | null>(null);
    const [pdfMenuTarget, setPdfMenuTarget] = useState<{ fileName?: string; markdownContent?: string } | null>(null);
    const peoplePickerRef = useRef<HTMLInputElement>(null);

    const openShareDialog = useCallback((payload: { fileName?: string; markdownContent?: string }) => {
        setShareTarget(payload);
        setShareRecipients([]);
        setShareSubject("PDF Export");
        setShareDialogOpen(true);
    }, []);

    const openPdfMenu = useCallback((ev: React.MouseEvent<HTMLElement>, payload: { fileName?: string; markdownContent?: string }) => {
        setPdfMenuAnchor(ev.currentTarget);
        setPdfMenuTarget(payload);
    }, []);

    const closePdfMenu = useCallback(() => {
        setPdfMenuAnchor(null);
        setPdfMenuTarget(null);
    }, []);

    const addRecipient = useCallback((value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return;
        setShareRecipients(prev => (
            prev.includes(trimmed) ? prev : [...prev, trimmed]
        ));
    }, []);

    const handleShareSend = useCallback(async () => {
        if (!shareTarget?.markdownContent) return;
        if (shareRecipients.length === 0) return;
        setShareSending(true);
        try {
            const token = tokenContext.getUserToken();
            const pdfClient = ClientHelper.createPdfClient(token);
            await pdfClient.share(new PdfShareRequest({
                recipients: shareRecipients,
                markdownContent: shareTarget.markdownContent,
                fileName: shareTarget.fileName,
                subject: shareSubject,
            }));
            setShareDialogOpen(false);
        } catch (err) {
            console.error("share failed", err);
        } finally {
            setShareSending(false);
        }
    }, [shareRecipients, shareSubject, shareTarget, tokenContext]);

    

    const getScrollParent = useCallback((node: HTMLElement | null): HTMLElement | null => {
        let current = node?.parentElement ?? null;
        while (current) {
            const style = window.getComputedStyle(current);
            const overflowY = style.overflowY;
            if (overflowY === "auto" || overflowY === "scroll") {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }, []);

    const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
        if (!bottomRef.current) return;
        bottomRef.current.scrollIntoView({ behavior, block: "end" });
    }, []);

    useEffect(() => {
        const node = containerRef.current;
        if (!node) return;
        const scrollParent = getScrollParent(node);
        if (!scrollParent) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollParent;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            autoScrollRef.current = distanceFromBottom <= 24;
        };

        handleScroll();
        scrollParent.addEventListener("scroll", handleScroll, { passive: true });
        return () => {
            scrollParent.removeEventListener("scroll", handleScroll);
        };
    }, [getScrollParent]);

    useLayoutEffect(() => {
        if (!autoScrollRef.current) return;
        scrollToBottom("auto");
    }, [filteredMessages.length, messages, scrollToBottom]);

    return (
        <div className="chat-container" ref={containerRef}>
            <div className="chat-toolbar">
                <PagingControls
                    enabled={useWindowing}
                    start={windowStart}
                    total={totalMessages}
                    pageSize={WINDOW_SIZE}
                    maxStart={maxWindowStart}
                    onPrev={handlePrevWindow}
                    onNext={handleNextWindow}
                />
            </div>
            {windowedMessages.map((msg, index) => (
                <React.Fragment key={windowStart + index}>
                    {msg.toolCalls !== undefined && msg.toolCalls?.length > 0 && <>
                        {msg.toolCalls.filter(tc =>
                            showToolCalls
                            || tc.callNeedsToBeValidated
                            || tc.approvalStatus === "approved"
                            || tc.approvalStatus === "rejected"
                        ).map((toolCall, index) => {
                            const isPending = toolCall.id ? pendingActions[toolCall.id] : undefined;
                            return <div className={`chat-bubble left`} key={index + "_tool"}>
                                {toolCall.uiFunctionCallSummary}
                                {toolCall.uiParameters && toolCall.uiParameters.length > 0 && (
                                    <div className="tool-params">
                                        {toolCall.uiParameters.map((param, paramIndex) => (
                                            <div className="tool-param" key={`${index}_param_${paramIndex}`}>
                                                <div className="tool-param-name">{param.name}</div>
                                                <div className="tool-param-value">{param.value ?? "—"}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {toolCall.callNeedsToBeValidated === true && toolCall.approvalStatus !== "approved" && toolCall.approvalStatus !== "rejected" && <>
                                    <span color="red">Der Aufruf wurde noch nicht ausgeführt und muss erst bestätigt werden</span>
                                    <Button
                                        disabled={isPending === "approving"}
                                        onClick={async () => {
                                            if (!toolCall.id) return;
                                            setPendingActions(prev => ({ ...prev, [toolCall.id!]: "approving" }));
                                            var client = ClientHelper.createNswagClient(tokenContext.getUserToken() as string)
                                            try {
                                                var approveRequestModel: IApproveToolCallRequestModel = {
                                                    history: messages,
                                                    toolCallIdToExecute: toolCall.id
                                                }
                                                var result = await client.approveToolCall(new ApproveToolCallRequestModel(approveRequestModel));
                                                if (result.history && Array.isArray(result.history)) {
                                                    approveToolCall(result.history);
                                                } else {
                                                    const updatedHistory = messages.map(m => {
                                                        const msg = ChatMessageModel.fromJS(m);
                                                        if (!msg.toolCalls) return msg;
                                                        msg.toolCalls = msg.toolCalls.map(tc => {
                                                            if (tc.id !== toolCall.id) return tc;
                                                            const updated = ToolCallViewModel.fromJS(tc);
                                                            updated.callNeedsToBeValidated = false;
                                                            updated.validationSignature = undefined;
                                                            updated.approvalStatus = "approved";
                                                            return updated;
                                                        });
                                                        return msg;
                                                    });
                                                    const turnMessages = Array.isArray(result.currentTurnMessages)
                                                        ? result.currentTurnMessages.map(m => ChatMessageModel.fromJS(m))
                                                        : [];
                                                    approveToolCall([...updatedHistory, ...turnMessages]);
                                                }
                                            } catch (e) {
                                                console.error(e)
                                            } finally {
                                                setPendingActions(prev => {
                                                    const next = { ...prev };
                                                    delete next[toolCall.id!];
                                                    return next;
                                                });
                                            }
                                        }} >Bestätigen</Button>
                                    <Button
                                        disabled={isPending === "rejecting"}
                                        onClick={() => {
                                            if (!toolCall.id) return;
                                            setPendingActions(prev => ({ ...prev, [toolCall.id!]: "rejecting" }));
                                            const nextHistory = messages.map(m => {
                                                const msg = ChatMessageModel.fromJS(m);
                                                if (!msg.toolCalls) return msg;
                                                msg.toolCalls = msg.toolCalls.map(tc => {
                                                    if (tc.id !== toolCall.id) return tc;
                                                    const updated = ToolCallViewModel.fromJS(tc);
                                                    updated.callNeedsToBeValidated = false;
                                                    updated.validationSignature = undefined;
                                                    updated.approvalStatus = "rejected";
                                                    return updated;
                                                });
                                                return msg;
                                            });
                                            approveToolCall(nextHistory);
                                            setPendingActions(prev => {
                                                const next = { ...prev };
                                                delete next[toolCall.id!];
                                                return next;
                                            });
                                        }}
                                    >Ablehnen</Button>
                                </>}
                                {toolCall.approvalStatus === "approved" && (
                                    <div className="tool-approval-status approved">Wurde bestätigt</div>
                                )}
                                {toolCall.approvalStatus === "rejected" && (
                                    <div className="tool-approval-status rejected">Wurde abgelehnt</div>
                                )}
                            </div>
                        })}
                    </>}
                    {(() => {
                        const tryParseJsonEnvelope = (value?: string | null): { content?: string; questions?: { id?: string; question?: string }[] } | null => {
                            if (!value || typeof value !== "string") return null;
                            const trimmed = value.trim();
                            if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;
                            try {
                                const parsed = JSON.parse(trimmed);
                                if (!parsed || typeof parsed !== "object") return null;
                                const questions = Array.isArray(parsed.questions) ? parsed.questions : [];
                                if (!("content" in parsed) && questions.length === 0) return null;
                                return parsed;
                            } catch {
                                return null;
                            }
                        };

                        const parsedEnvelope = tryParseJsonEnvelope(msg.content);
                        const effectiveContent = parsedEnvelope?.content ?? msg.content;
                        const clarificationQuestions = parsedEnvelope?.questions ?? msg?.clarification?.questions ?? [];
                        const clarificationContent = clarificationQuestions.length
                            ? `**Klärungsfragen:**\n${clarificationQuestions.map(q => `- ${q?.question ?? ''}`).join('\n')}`
                            : '';
                        const contentToRender = hasRenderableContent(effectiveContent)
                            ? effectiveContent
                            : (clarificationContent || effectiveContent);

                        return hasRenderableContent(contentToRender) ? (
                            <ChatHistoryMessage
                                key={windowStart + index}
                                msg={msg}
                                effectiveContent={contentToRender}
                                hasRenderableContent={hasRenderableContent}
                                normalizeContent={normalizeContent}
                                markdownComponents={markdownComponents}
                                openPdfMenu={openPdfMenu}
                            />
                        ) : null;
                    })()}

                </React.Fragment>
            ))}
            <Dialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                disableAutoFocus
                disableEnforceFocus
                disableRestoreFocus
                TransitionProps={{
                    onEntered: () => {
                        peoplePickerRef.current?.focus();
                        peoplePickerRef.current?.select?.();
                    },
                }}
            >
                <DialogTitle>PDF per E-Mail teilen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <PeoplePicker
                            label="Empfänger (Name oder E-Mail)"
                            onChange={addRecipient}
                            disabled={shareSending}
                            inputRef={peoplePickerRef}
                        />
                        {shareRecipients.length > 0 && (
                            <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                    Empfänger:
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {shareRecipients.map((item) => (
                                        <Chip
                                            key={item}
                                            label={item}
                                            onDelete={() => setShareRecipients(prev => prev.filter(p => p !== item))}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        )}
                        <TextField
                            label="Betreff"
                            value={shareSubject}
                            onChange={(ev) => setShareSubject(ev.target.value)}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        variant="contained"
                        disabled={shareSending || shareRecipients.length === 0}
                        onClick={handleShareSend}
                    >
                        {shareSending ? "Sende..." : "Senden"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                anchorEl={pdfMenuAnchor}
                open={Boolean(pdfMenuAnchor)}
                onClose={closePdfMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem
                    onClick={() => {
                        if (!pdfMenuTarget?.markdownContent) return;
                        handlePdfDownload(pdfMenuTarget);
                        closePdfMenu();
                    }}
                >
                    PDF herunterladen
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (!pdfMenuTarget?.markdownContent) return;
                        openShareDialog(pdfMenuTarget);
                        closePdfMenu();
                    }}
                >
                    PDF teilen
                </MenuItem>
            </Menu>
            <div ref={bottomRef} />
        </div>
    );
};

export const ChatHistoryView = React.memo(ChatHistoryViewImpl);
